import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { asDateInTZ } from '@/utilities/convertToISO';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { formatInTimeZone } from 'date-fns-tz';
import { useLazyQuery } from '@apollo/client';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { useClientUser } from '@/hooks';
import { OUTBOUND_MANIFEST_TYPES } from '@/constants/manifestStatuses';
import { GET_RACKS } from '../graphql';

const EditManifest = ({ manifest, loading, callbacks }) => {
    const { locations: warehouses } = useClientUser();
    const isOutbound = OUTBOUND_MANIFEST_TYPES.includes(manifest?.type);

    const [updates, setUpdates] = useState({});
    const [stagingZoneOptions, setStagingZoneOptions] = useState([]);
    const update = (patch) => setUpdates((prev) => ({ ...prev, ...patch }));

    const updated = useMemo(() => {
        return {
            ...(manifest || {}),
            // Pre-epic manifests have a null name; fall back to the label the page already shows so Edit
            // opens valid instead of demanding a rename before any other change can be saved. Only
            // `updates` is saved, so an untouched fallback is never written.
            name: manifest?.name || (manifest?.manifest_number ? `Manifest ${manifest.manifest_number}` : ''),
            ...updates,
        };
    }, [manifest, updates]);

    const isValid = useMemo(() => {
        return ['name', 'receiving_date', 'warehouse_id'].every((attr) => updated[attr]);
    }, [updated]);

    // Populate the staging-zone dropdown with the warehouse's pool locations (same source the
    // Set Staging Zone dialog uses).
    const [getRacks] = useLazyQuery(GET_RACKS, {
        onCompleted: ({ location }) => {
            const pools = (location?.warehouse_racks || [])
                .filter((rack) => rack?.isPool)
                .map((rack) => ({ label: rack?.customName || `Pool ${rack.rackIdentifier}` }));
            setStagingZoneOptions(pools);
        },
        onError: (error) => console.error('Error fetching staging zones:', error),
    });

    useEffect(() => {
        if (isOutbound && updated.warehouse_id) {
            getRacks({ variables: { location_id: updated.warehouse_id } });
        }
    }, [isOutbound, updated.warehouse_id]);

    const cancel = () => {
        setUpdates({});
        callbacks.editManifest(null);
    };

    const save = () => {
        callbacks
            .saveManifest({
                manifest_id: manifest.manifest_id,
                ...updates,
            })
            .then(() => {
                setUpdates({});
                callbacks.editManifest(null);
            });
    };

    return (
        <NavResponsiveModal open={!!manifest} onClose={cancel}>
            <ModalHeader title={'Edit Manifest'} subtitle={manifest?.manifest_number || '0000'} onClose={cancel} />
            <ModalContent
                css={css`
                    width: 600px;
                `}
            >
                <Grid container direction="column">
                    <TextField
                        variant="outlined"
                        required
                        label="Manifest Name"
                        name="name"
                        fullWidth
                        value={updated.name || ''}
                        error={!updated.name}
                        onChange={(e) => update({ name: e.target.value })}
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    />
                    <TextField
                        variant="outlined"
                        label="Origin"
                        name="origin"
                        fullWidth
                        value={updated.origin || ''}
                        onChange={(e) => update({ origin: e.target.value })}
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    />
                    <TextField
                        variant="outlined"
                        label="Destination"
                        name="origin"
                        fullWidth
                        value={updated.destination || ''}
                        onChange={(e) => update({ destination: e.target.value })}
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    />
                    <TextField
                        type="date"
                        variant="outlined"
                        label="Receiving Date"
                        name="receiving_date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={
                            updated.receiving_date
                                ? formatInTimeZone(new Date(updated.receiving_date), 'UTC', 'yyyy-MM-dd')
                                : '--'
                        }
                        onChange={(e) =>
                            update({
                                receiving_date: e.target.value ? asDateInTZ(e.target.value, 'UTC').toISOString() : null,
                            })
                        }
                        css={css`
                            margin-bottom: 1rem;
                        `}
                        error={!updated.receiving_date}
                    />
                    {warehouses.length > 0 ? (
                        <TextField
                            variant="outlined"
                            required
                            select
                            label="Warehouse"
                            name="warehouse_id"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={updated.warehouse_id || ''}
                            onChange={(e) => update({ warehouse_id: e.target.value })}
                            error={!updated.warehouse_id}
                        >
                            {warehouses.map((warehouse) => (
                                <MenuItem key={warehouse.location_id} value={warehouse.location_id}>
                                    {warehouse.location_name}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : (
                        <Alert severity="warning">
                            <AlertTitle>No Warehouse(s)</AlertTitle>Please add a new warehouse to get started
                        </Alert>
                    )}
                    {isOutbound && (
                        <Autocomplete
                            freeSolo
                            options={stagingZoneOptions}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option?.label?.toString() || ''
                            }
                            value={updated.staging_zone || ''}
                            onChange={(event, value) =>
                                update({ staging_zone: typeof value === 'string' ? value : value?.label || '' })
                            }
                            onInputChange={(event, value) => update({ staging_zone: value })}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" label="Staging Zone" name="staging_zone" />
                            )}
                            css={css`
                                margin-top: 1rem;
                            `}
                        />
                    )}
                </Grid>
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading.updateLoading} onClick={cancel}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading.updateLoading || !isValid} onClick={save}>
                    Save
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default EditManifest;
